<template>
  <el-dialog
    v-model="dialogVisible"
    title="选择加盟商"
    width="600px"
    append-to-body
    destroy-on-close
  >
    <div class="search-box mb-4">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索加盟商名称"
        clearable
        @keyup.enter="handleSearch"
        @clear="handleSearch"
      >
        <template #append>
          <el-button @click="handleSearch">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </el-button>
        </template>
      </el-input>
    </div>

    <div class="franchisee-list max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
      <div v-if="loading" class="p-4 text-center text-slate-500">
        加载中...
      </div>
      <template v-else-if="franchiseeList.length > 0">
        <div
          v-for="item in franchiseeList"
          :key="item.id"
          class="p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
          :class="{ 'bg-primary-50': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <div class="flex items-center gap-3">
            <el-checkbox
              :model-value="isSelected(item)"
              @click.stop
              @change="toggleSelect(item)"
            />
            <div class="flex-1">
              <div class="font-medium text-slate-800">{{ item.name }}</div>
              <div class="text-xs text-slate-500 mt-0.5">
                <span v-if="item.ownerUserName">负责人: {{ item.ownerUserName }}</span>
                <span v-if="item.statusDesc" class="ml-2">状态: {{ item.statusDesc }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="p-8 text-center text-slate-500">
        暂无数据
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between">
        <div class="text-sm text-slate-500">
          已选择 <span class="font-medium text-primary-600">{{ selectedList.length }}</span> 个加盟商
        </div>
        <div class="flex items-center gap-2">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleConfirm">确定</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getFranchiseePage } from '@/api/franchisee'

const props = defineProps({
  modelValue: {
    type: [Array, String, Number],
    default: () => []
  },
  multiple: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = ref(false)
const loading = ref(false)
const searchKeyword = ref('')
const franchiseeList = ref([])

const selectedList = computed(() => {
  if (!props.modelValue) return []
  return Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
})

const isSelected = (item) => {
  return selectedList.value.some(s => (typeof s === 'object' && s.id === item.id) || s === item.id || s === String(item.id))
}

const toggleSelect = (item) => {
  const current = [...selectedList.value]
  const index = current.findIndex(s => (typeof s === 'object' && s.id === item.id) || s === item.id || s === String(item.id))

  if (props.multiple) {
    if (index > -1) {
      current.splice(index, 1)
    } else {
      current.push({ id: item.id, name: item.name })
    }
  } else {
    if (index > -1) {
      current.length = 0
    } else {
      current.length = 0
      current.push({ id: item.id, name: item.name })
    }
  }

  emit('update:modelValue', props.multiple ? current : current[0] || null)
}

const handleSearch = async () => {
  await loadFranchisees()
}

const loadFranchisees = async () => {
  try {
    loading.value = true
    const res = await getFranchiseePage({
      name: searchKeyword.value || undefined,
      status: 3, // 只查询正式加盟商
      current: 1,
      size: 100
    })
    franchiseeList.value = res.records || []
  } catch (error) {
    console.error('加载加盟商列表失败:', error)
  } finally {
    loading.value = false
  }
}

const handleConfirm = () => {
  emit('update:modelValue', props.multiple ? selectedList.value : selectedList.value[0] || null)
  dialogVisible.value = false
}

// 打开弹窗时加载数据
watch(dialogVisible, (val) => {
  if (val) {
    loadFranchisees()
  }
})

// 提供打开选择器的方法
const open = () => {
  dialogVisible.value = true
}

defineExpose({ open })
</script>
